// src/lib/charts/client.ts

"use client"

/**
 * Client-only chart library exports
 * 
 * This file contains all runtime chart library exports that require DOM access.
 * These libraries (Recharts, ReactFlow, D3 DOM utilities) assume a browser environment
 * and cannot be safely imported in server components.
 * 
 * Problem solved:
 * Previously, these runtime values were mixed with types in a shared barrel export,
 * causing server components to pull the entire chart runtime into the server build.
 * This led to massive bundle sizes and heap OOM errors in Next.js 15's stricter
 * React Server Component boundaries.
 * 
 * Solution:
 * - Client components import from this file for runtime chart functionality
 * - Server components import from ../index.ts for types and pure utilities only
 * - This separation prevents DOM-dependent libraries from being bundled server-side
 */

// Recharts – runtime chart components (DOM-dependent)
export {
  Line,
  LineChart,
  BarChart,
  Bar,
  Area,
  AreaChart,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
  XAxis,
  YAxis,
  ZAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceArea,
  ReferenceLine,
  ReferenceDot,
  Dot,
  Scatter,
  ScatterChart,
  ComposedChart,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Treemap,
  Brush,
  ErrorBar,
  FunnelChart,
  Funnel,
  Sankey,
  LabelList,
  Rectangle
} from "recharts"

// ReactFlow – runtime flow diagram components (DOM-dependent)
export {
  ReactFlow,
  Background,
  Controls,
  MarkerType,
  Position,
  ReactFlowProvider,
  useNodesState,
  useEdgesState,
  Handle,
  BackgroundVariant
} from "@xyflow/react"

// Included to avoid server bundling types from @xyflow/react
export type {
  Node,
  NodeTypes,
  Edge as ReactFlowEdge
} from "@xyflow/react"

// D3 – DOM-dependent utilities (selection, zoom, drag, etc.)
// Note: Many D3 modules are server-safe, but these require DOM access
export {
  select,
  selectAll,
  zoom,
  zoomIdentity,
  forceSimulation,
  forceCenter,
  forceCollide,
  forceLink,
  forceManyBody,
  forceX,
  forceY,
  color,
  drag,
  scaleLinear,
  scaleTime,
  axisBottom,
  axisLeft,
  axisRight,
  axisTop,
  line,
  area,
  pie,
  arc,
  stack,
  symbol,
  symbolCircle,
  symbolSquare,
  symbolTriangle,
  transition,
  easeLinear,
  easeCubic
} from "d3"

// Custom tooltip utilities (client-only)
export {
  getOrCreateTooltipWrapper,
  __clearTooltipWrapperCacheForTests,
  type TooltipPayloadItem,
  type CustomTooltipProps
} from './tooltip'
